import { NonEmptyArray, Option, pipe, ReadonlyArray } from "fp";

import React from "react";

import * as Data from "~/Data";
import { Timeline } from "~/Timeline";

export type Section<Event extends Timeline.Event.Any> = {
  readonly Menu: Component;
} & Timeline.Event.Props<Event>;

export type RouteValue = string;

export namespace Section {
  export type Config<Event extends Timeline.Event.Any> = Omit<
    Section<Event>,
    "timeline" | "event"
  >;

  export type Definition = (timeline: Timeline.Remote) => {
    readonly Menu: Component;
    readonly Rows: Component;
  };

  export const use =
    <Event extends Timeline.Event.Any>(
      route: RouteValue,
      events: readonly Event[]
    ) =>
    ({ Menu, ...config }: Config<Event>): Definition =>
    (timeline) => {
      const signature = eventsSignature(route, events);
      return pipe(
        timeline,
        Data.Remote.map(useRows(config, events)),
        Data.Remote.getOrElse((): readonly Component[] =>
          Timeline.Rows.empty()
        ),
        ReadonlyArray.mapWithIndex((index, Row) => <Row key={index} />),
        (rows) => ({
          Rows: React.useCallback(() => <>{rows}</>, [signature]),
          Menu: React.useCallback(
            () => (
              <Timeline.Menu.Item rowCount={rows.length}>
                <Menu />
              </Timeline.Menu.Item>
            ),
            [signature]
          ),
        })
      );
    };

  export const eventsSignature = <Event extends Timeline.Event.Any>(
    route: RouteValue,
    events: readonly Event[]
  ) =>
    events.length > 0
      ? `${route}:${events.length}:${events[0].interval.start.dateTime}:${
          events[events.length - 1].interval.start.dateTime
        }`
      : route;

  export const useRows =
    <Event extends Timeline.Event.Any>(
      config: Timeline.Event.Config<Event>,
      events: readonly Event[]
    ) =>
    (timeline: Timeline): readonly Component[] =>
      pipe(
        Timeline.Rows.fromEvents(config, events),
        NonEmptyArray.fromReadonlyArray,
        Option.fold(
          () => Timeline.Rows.empty(),
          ReadonlyArray.reduce([] as readonly Component[], (previous, row) => [
            ...previous,
            () => <Timeline.Row {...config} timeline={timeline} row={row} />,
          ])
        )
      );
}
